<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useSaleTypesStore } from 'dashboard/stores/saleTypes';

const { t } = useI18n();
const saleTypesStore = useSaleTypesStore();

const isOpen = ref(false);
const newTypeName = ref('');
const isAdding = ref(false);
const isSaving = ref(false);

async function open() {
  isOpen.value = true;
  newTypeName.value = '';
  if (!saleTypesStore.types.length) {
    await saleTypesStore.fetch();
  }
}

function close() {
  isOpen.value = false;
}

async function handleAdd() {
  const name = newTypeName.value.trim();
  if (!name) return;
  if (saleTypesStore.types.includes(name)) {
    useAlert(t('SALES.SALE_TYPE.ERROR'), { variant: 'error' });
    return;
  }

  isSaving.value = true;
  try {
    await saleTypesStore.save([...saleTypesStore.types, name]);
    newTypeName.value = '';
    useAlert(t('SALES.SALE_TYPE.SUCCESS_SAVE'));
  } catch {
    useAlert(t('SALES.SALE_TYPE.ERROR'), { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
}

async function handleRemove(typeToRemove) {
  isSaving.value = true;
  try {
    await saleTypesStore.save(saleTypesStore.types.filter(t => t !== typeToRemove));
    useAlert(t('SALES.SALE_TYPE.SUCCESS_REMOVE'));
  } catch {
    useAlert(t('SALES.SALE_TYPE.ERROR'), { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
}

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

      <div class="relative z-10 w-full max-w-sm flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('SALES.SALE_TYPE.MANAGE') }}
          </h2>
          <button
            class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x text-lg" />
          </button>
        </div>

        <!-- List -->
        <div class="px-5 py-4 flex flex-col gap-2 max-h-72 overflow-y-auto">
          <div v-if="saleTypesStore.isFetching" class="flex justify-center py-4">
            <span class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-9" />
          </div>

          <p v-else-if="!saleTypesStore.types.length" class="text-sm text-n-slate-9 py-4 text-center">
            {{ $t('SALES.SALE_TYPE.EMPTY') }}
          </p>

          <div
            v-for="type in saleTypesStore.types"
            v-else
            :key="type"
            class="flex items-center justify-between rounded-lg px-3 py-2 bg-n-alpha-3 border border-n-weak"
          >
            <span class="text-sm text-n-slate-12">{{ type }}</span>
            <button
              class="p-1 rounded text-n-slate-9 hover:text-s-red-500 transition-colors"
              :disabled="isSaving"
              @click="handleRemove(type)"
            >
              <span class="i-lucide-trash-2 text-sm" />
            </button>
          </div>
        </div>

        <!-- Add new -->
        <div class="px-5 py-4 border-t border-n-weak flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11">
            {{ $t('SALES.SALE_TYPE.ADD') }}
          </label>
          <div class="flex gap-2">
            <input
              v-model="newTypeName"
              type="text"
              :placeholder="$t('SALES.SALE_TYPE.NAME_PLACEHOLDER')"
              class="flex-1 rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand"
              @keydown.enter="handleAdd"
            />
            <button
              class="px-3 py-2 text-sm rounded-lg bg-n-brand text-white hover:bg-n-brand-dark transition-colors disabled:opacity-50 flex items-center gap-1"
              :disabled="isSaving || !newTypeName.trim()"
              @click="handleAdd"
            >
              <span v-if="isSaving" class="i-lucide-loader-circle animate-spin text-sm" />
              <span v-else class="i-lucide-plus text-sm" />
              {{ $t('SALES.SALE_TYPE.SAVE') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
